/**
 * InlineText — mini éditeur visuel inline pour les textes publics.
 *
 * - En mode PUBLIC : affiche simplement le texte comme un composant standard.
 * - En mode ADMIN (user authentifié) : bouton crayon discret au survol,
 *   clic → mini pop-in d'édition (textarea + Enregistrer/Annuler).
 * - Enregistrement via `onSave(newValue)` → à câbler avec l'API /admin/content.
 * - Les modifications sont propagées immédiatement (optimistic update).
 *
 * Usage :
 *   <InlineText path="experiences.petit-dejeuner.sub" value={sub} onSave={saveText}
 *               as="p" className="italic text-refuge-soft" multiline />
 */
import { useEffect, useRef, useState } from "react";
import { Pencil, Save, X, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function InlineText({
  value,
  onSave,
  path,
  as: Tag = "span",
  className = "",
  multiline = false,
  placeholder = "",
  ...rest
}) {
  const { user } = useAuth();
  const isAdmin = !!user;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => { setDraft(value ?? ""); }, [value]);

  // Ferme si clic à l'extérieur pendant l'édition
  useEffect(() => {
    if (!editing) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setEditing(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [editing]);

  if (!isAdmin) {
    // Rendu public standard
    return <Tag className={className} {...rest}>{value}</Tag>;
  }

  const save = async () => {
    if (draft === value) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      setError(e?.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <span ref={wrapRef} className="relative inline-block w-full" data-testid={`inline-edit-${path}`}>
        {multiline ? (
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            className="w-full min-h-[80px] p-3 border border-refuge-ruby rounded-md font-inherit text-inherit bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-refuge-ruby/40"
            style={{ font: "inherit", color: "inherit" }}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            className="w-full p-2 border border-refuge-ruby rounded-md font-inherit text-inherit bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-refuge-ruby/40"
            style={{ font: "inherit", color: "inherit" }}
          />
        )}
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={save}
            disabled={busy}
            data-testid={`inline-save-${path}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-refuge-ruby text-white text-xs font-body hover:bg-refuge-ruby/90 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Enregistrer
          </button>
          <button
            onClick={() => { setDraft(value ?? ""); setEditing(false); }}
            disabled={busy}
            data-testid={`inline-cancel-${path}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-refuge-border text-refuge-text text-xs font-body hover:bg-refuge-bg"
          >
            <X size={12} /> Annuler
          </button>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      </span>
    );
  }

  // Mode admin, non éditant : affiche le texte + bouton crayon au survol
  return (
    <Tag
      className={`${className} group/inline relative cursor-pointer transition-all`}
      data-testid={`inline-view-${path}`}
      onClick={(e) => {
        // Évite déclenchement si clic sur un enfant interactif (Link, button)
        if (e.target.closest("a,button")) return;
        setEditing(true);
      }}
      style={{ outline: "1px dashed transparent", outlineOffset: 4, borderRadius: 4 }}
      onMouseEnter={(e) => (e.currentTarget.style.outlineColor = "rgba(139,37,48,0.30)")}
      onMouseLeave={(e) => (e.currentTarget.style.outlineColor = "transparent")}
      {...rest}
    >
      {value || <span className="opacity-40 italic">{placeholder}</span>}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        data-testid={`inline-btn-${path}`}
        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-refuge-ruby text-white flex items-center justify-center opacity-0 group-hover/inline:opacity-100 transition-opacity shadow-md"
        title={`Éditer ${path}`}
      >
        <Pencil size={11} strokeWidth={1.6} />
      </button>
    </Tag>
  );
}
